import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { CustomerLookup } from '../types/api';
import type { Customer } from '../types/customer';
import { useCustomersApi } from './use-customers-api';

/**
 * Query key for useGetCustomer
 */
export const QK_GET_CUSTOMER = (lookup: CustomerLookup) => ['customer', lookup] as const;

/** Lookup builders per identifier type; email is URL-encoded so special characters are safe in query keys and API calls. */
export const CUSTOMER_LOOKUP: Record<CustomerLookup['by'], (identifier: string) => CustomerLookup> =
  {
    email: (email) => ({
      by: 'email',
      identifier: encodeURIComponent(email),
    }),
    platform: (platformId) => ({
      by: 'platform',
      identifier: platformId,
    }),
    sparklayer: (sparklayerId) => ({
      by: 'sparklayer',
      identifier: sparklayerId,
    }),
  };

type GetCustomerOptions = Partial<Omit<UseQueryOptions<Customer>, 'queryKey' | 'queryFn'>>;

export function useGetCustomer(lookup: CustomerLookup, options?: GetCustomerOptions) {
  const { customersApi } = useCustomersApi();

  return useQuery({
    ...options,
    queryKey: QK_GET_CUSTOMER(lookup),
    queryFn: () => customersApi.customer(lookup).get(),
    enabled: options?.enabled ?? !!lookup.identifier,
  });
}
